import { useCallback, useEffect, useState } from 'react'
import { sharedCanvaStatus, refreshCanvaStatus, connectionGap, canvaConnectUrl } from './canvaSource.js'
import styles from './Studio.module.css'

/**
 * THE DESIGN FEED, STATED OUT LOUD.
 *
 * Four of the thirteen steps — bezel & top, both side shanks, the background —
 * are element pickers, and a picker that quietly shows nothing is the single
 * fastest way to make this builder feel broken. So the studio says which feed
 * it is on before the operator opens the first picker, and when the feed is
 * down it names the exact thing that is missing instead of rendering an empty
 * folder.
 *
 * The one-click connect is real: `GET /api/canva/oauth/start` returns Canva's
 * consent URL and the BACKEND handles the callback and stores the rotating
 * refresh token. No Canva credential ever touches client code (hard rules #2
 * and #3) — this button only opens a tab.
 *
 * Whatever the feed is doing, the manual path is never blocked: every picker
 * carries its own always-visible reference field and upload button, so a
 * component can always be supplied.
 */
export default function CanvaConnection() {
  const [status, setStatus] = useState(null)
  const [connecting, setConnecting] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    let alive = true
    sharedCanvaStatus().then((s) => { if (alive) setStatus(s) })
    return () => { alive = false }
  }, [])

  const connect = useCallback(async () => {
    setConnecting(true)
    setErr('')
    try {
      const url = await canvaConnectUrl()
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (e) {
      setErr(e?.message || 'Could not start the Canva authorization.')
    } finally {
      setConnecting(false)
    }
  }, [])

  const recheck = useCallback(async () => {
    setStatus(null)
    setStatus(await refreshCanvaStatus())
  }, [])

  if (!status) return null

  const gap = connectionGap(status)

  if (!gap) {
    return (
      <p className={styles.feedOk}>
        <span className={`${styles.badge} ${styles.badgeOk}`}>{status.demo ? 'demo library' : 'live Canva'}</span>
        {status.demo
          ? 'Demo mode — the pickers show a canned tree, badged, so it is never mistaken for the real library.'
          : 'The element pickers are reading the SR Artwork Engine folders live. Bezel & top, both shanks and the backgrounds list real designs with thumbnails.'}
      </p>
    )
  }

  return (
    <div className={styles.feedGap}>
      <div className={styles.feedGapHead}>
        <span className={`${styles.badge} ${styles.badgeWarn}`}>manual only</span>
        <strong>{gap.headline}</strong>
      </div>
      <p className={styles.note}>{gap.fix}</p>
      <p className={styles.note}>
        <strong>Nothing is blocked.</strong> Every element step has an always-visible <em>Attach reference</em> field and an
        <em> Upload</em> button — an upload goes to HubSpot File Manager, so the asset is owned rather than a free-floating
        link.
      </p>
      <div className={styles.feedActions}>
        {gap.canConnect && (
          <button type="button" className="btn btn-primary" onClick={connect} disabled={connecting}>
            {connecting ? 'Opening Canva…' : 'Connect Canva'}
          </button>
        )}
        <button type="button" className="btn" onClick={recheck}>Re-check the feed</button>
      </div>
      {err && <p className={styles.warnNote}>{err}</p>}
    </div>
  )
}
